import { AlertCircle, Loader2 } from 'lucide-react';
import { useI18n } from '@/lib/i18n';
import { formatSignedUsd } from '@/lib/money-format';
import type { ExpiringSoonStatus } from '../wallet-format';
import { formatHours } from './balance-estimate';
import { DEFAULT_SCENARIO } from './calculator-scenarios';
import { roundUsd } from './cost-model';
import { RecentRecords } from './RecentRecords';

/** What the hours estimate is priced at: the visitor's own 30-day average, or the calculator's opening preset. */
export type HoursBasis = { kind: 'average'; usdPerHour: number } | { kind: 'scenario' };

export interface WalletStatus {
  balanceMicroUsd: number;
  rates: Record<string, number>;
  frozen: boolean;
  /** Null when nothing is within the notice horizon. */
  expiringSoon: ExpiringSoonStatus | null;
  /** 30-day spend. The payload also carries a deprecated `usage` alias with
   *  the same value, kept only for already-shipped clients — read this one. */
  last30DaysUsageMicroUsd: number;
}

/**
 * The balance half of the account card.
 *
 * A PANEL, not a card: `AccountCard` owns the surface. Balance and top-up share
 * one card because "how much do I have" and "add more" are one thought — and
 * because two cards side by side put a short one next to a tall one, which is
 * what left a visible pocket of nothing on the page.
 *
 * It stays its own component all the same. Its loading and error states are
 * independent of the top-up half's: a wallet-status failure must not take the
 * payment form down with it, and folding the two into a single component would
 * have cost exactly that.
 *
 * Beside the top-up form the balance alone left most of the panel empty, so it
 * also answers the next two questions: how long the money lasts, and whether
 * the last payment arrived (the latest records, with their receipts). The
 * expiry notice moved to the records card's header, next to the credits it is
 * about.
 */
export function BalancePanel({ status, loading, error, hoursLeft, hoursBasis, onShowAllRecords }: {
  status: WalletStatus | null;
  loading: boolean;
  error: string | null;
  /** Hours the balance lasts at `hoursBasis`; null while no rate is known. */
  hoursLeft: number | null;
  hoursBasis: HoursBasis;
  onShowAllRecords: () => void;
}) {
  const { t, locale } = useI18n();

  return (
    <div className="wallet-panel wallet-panel--balance">
      <span className="wallet-panel__eyebrow">{t('dashboard.wallet.balance')}</span>

      {error ? (
        <div className="wallet-card__frozen-warning">
          <AlertCircle size={16} />
          {error}
        </div>
      ) : loading ? (
        <div className="wallet-card__loading">
          <Loader2 className="animate-spin" size={20} />
        </div>
      ) : (
        <>
          <span className="wallet-panel__amount">
            {status ? formatSignedUsd(status.balanceMicroUsd) : '$0.00'}
          </span>

          {status?.frozen && (
            <div className="wallet-card__frozen-warning">
              <AlertCircle size={16} />
              {t('dashboard.wallet.accountFrozen')}
            </div>
          )}

          <div className="wallet-panel__stats">
            {/* An estimate, and labelled with what it is priced at: the
                visitor's own 30-day average, or the preset the cost calculator
                opens on (balance-estimate.ts). Not shown at a zero balance,
                where "≈ 0 hours" says nothing the balance does not. */}
            {hoursLeft !== null && hoursLeft > 0 && (
              <div className="wallet-panel__stat">
                <span className="wallet-panel__stat-label">
                  {hoursBasis.kind === 'average'
                    ? t('dashboard.wallet.hoursLeftAverage').replace(
                        '{rate}',
                        `$${roundUsd(hoursBasis.usdPerHour, 2).toFixed(2)}`
                      )
                    : t('dashboard.wallet.hoursLeft').replace(
                        '{scenario}',
                        // The preset `defaultScenarioUsdPerHour` prices, named from the
                        // same constant so the label can never name another.
                        t(`dashboard.wallet.pricing.scenario.${DEFAULT_SCENARIO}.title`)
                      )}
                </span>
                <span className="wallet-panel__stat-value">
                  {t('dashboard.wallet.hoursValue').replace('{hours}', formatHours(hoursLeft, locale))}
                </span>
              </div>
            )}
            <div className="wallet-panel__stat">
              <span className="wallet-panel__stat-label">{t('dashboard.wallet.last30Days')}</span>
              <span className="wallet-panel__stat-value">
                {formatSignedUsd(status?.last30DaysUsageMicroUsd ?? 0)}
              </span>
            </div>
          </div>
        </>
      )}

      <RecentRecords onShowAll={onShowAllRecords} />
    </div>
  );
}
